import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listStringersArticles } from '../services/stringersApi';
import { normalizeMarkdownContent } from '../utils/markdownContent';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocalDate } from '../utils/formatDate';
import './StringersPage.css';

function excerpt(text, maxLen = 160) {
  const plain = normalizeMarkdownContent(text || '')
    .replace(/[#*_`>[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen)}…`;
}

const StringersPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useDocumentTitle('Stringers');

  useEffect(() => {
    setLoading(true);
    setError(null);
    listStringersArticles(page, 12)
      .then((data) => {
        setArticles(data.content || []);
        setTotalPages(data.totalPages || 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="reader-page">
      <div className="container">
        <header className="reader-header stringers-header">
          <div className="reader-header-main">
            <p className="reader-kicker">Writer-published</p>
            <h1>Stringers</h1>
            <p className="reader-lead">
              Articles and NewsAgent Shorts from DarkNews researchers and the platform channel.
            </p>
          </div>
        </header>

        {loading && <p className="reader-status">Loading articles…</p>}
        {error && <p className="reader-error">{error}</p>}

        {!loading && !error && articles.length === 0 && (
          <div className="stringers-empty reader-empty">
            <h2>No published articles yet</h2>
            <p>
              Sign in to <Link to="/research">Research</Link>, draft an article, and publish to your
              author channel.
            </p>
          </div>
        )}

        {!loading && !error && articles.length > 0 && (
          <ul className="stringers-grid">
            {articles.map((article) => {
              const href =
                article.authorSlug && article.id
                  ? `/authors/${article.authorSlug}/${article.id}`
                  : null;
              const hasVideo = Boolean(article.mediaUrl);
              const poster = resolveMediaUrl(article.posterUrl);
              const mediaBlock = hasVideo ? (
                href ? (
                  <Link to={href} className="stringers-card-media" aria-label={`Play ${article.title}`}>
                    {poster ? (
                      <img src={poster} alt="" className="stringers-card-poster" />
                    ) : (
                      <div className="stringers-card-poster-fallback" />
                    )}
                    <span className="stringers-card-play">▶</span>
                    {article.articleType === 'newsagent_short' && (
                      <span className="stringers-card-badge">Short</span>
                    )}
                  </Link>
                ) : (
                  <div className="stringers-card-media">
                    {poster ? (
                      <img src={poster} alt="" className="stringers-card-poster" />
                    ) : (
                      <div className="stringers-card-poster-fallback" />
                    )}
                    <span className="stringers-card-play">▶</span>
                  </div>
                )
              ) : null;
              return (
                <li
                  key={article.id}
                  className={`stringers-card${hasVideo ? ' stringers-card--video' : ''}`}
                >
                  {mediaBlock}
                  {href ? (
                    <Link to={href} className="stringers-card-title">
                      {article.title}
                    </Link>
                  ) : (
                    <h2 className="stringers-card-title-plain">{article.title}</h2>
                  )}
                  <p className="stringers-card-meta">
                    {article.authorName || 'Author'}
                    {article.publishedAt ? ` · ${formatLocalDate(article.publishedAt)}` : ''}
                    {hasVideo ? ' · Video' : ''}
                  </p>
                  <p className="stringers-card-excerpt">
                    {excerpt(article.summary || article.content)}
                  </p>
                  {article.authorSlug && (
                    <Link to={`/authors/${article.authorSlug}`} className="stringers-card-author">
                      View channel →
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {totalPages > 1 && (
          <nav className="reader-pagination" aria-label="Stringers pagination">
            <button
              type="button"
              className="reader-btn-secondary"
              disabled={page <= 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span className="reader-page-indicator">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              className="reader-btn-secondary"
              disabled={page >= totalPages - 1 || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default StringersPage;
